import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { REKINDERLogo } from "@/components/brand-logo";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

async function apiPost(path: string, body: object) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Request failed");
  return data;
}

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-[#F5F2ED] border border-[#DDD8D0] focus:outline-none focus:ring-2 focus:ring-[#2D7A78]/30 focus:border-[#2D7A78] transition-all placeholder:text-[#1A3535]/30 text-[#1A3535] text-sm";

function PasswordInput({ placeholder, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        className={inputCls + " pr-12"}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#2D7A78] transition-colors"
        tabIndex={-1}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

type Mode = "login" | "register" | "forgot";

export function AuthDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("login");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const loginSchema = useMemo(() => z.object({
    email: z.string().email(t("auth.emailInvalid")),
    password: z.string().min(1, t("auth.passwordRequired")),
  }), [t]);

  const registerSchema = useMemo(() => z.object({
    name: z.string().min(2, t("auth.nameTooShort")),
    email: z.string().email(t("auth.emailInvalid")),
    password: z.string().min(8, t("auth.passwordTooShort")),
    confirmPassword: z.string(),
  }).refine((d) => d.password === d.confirmPassword, {
    message: t("auth.passwordMismatch"),
    path: ["confirmPassword"],
  }), [t]);

  const forgotSchema = useMemo(() => z.object({
    email: z.string().email(t("auth.emailInvalid")),
  }), [t]);

  type LoginData = z.infer<typeof loginSchema>;
  type RegisterData = z.infer<typeof registerSchema>;
  type ForgotData = z.infer<typeof forgotSchema>;

  const loginForm = useForm<LoginData>({ resolver: zodResolver(loginSchema) });
  const registerForm = useForm<RegisterData>({ resolver: zodResolver(registerSchema) });
  const forgotForm = useForm<ForgotData>({ resolver: zodResolver(forgotSchema) });

  const switchMode = (m: Mode) => {
    setMode(m);
    setForgotSent(false);
    loginForm.clearErrors();
    registerForm.clearErrors();
    forgotForm.clearErrors();
  };

  const onLogin = async (data: LoginData) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      toast({ title: t("auth.welcomeBackToast"), description: t("auth.signedIn") });
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t("auth.signInTab") + " failed. Please try again.";
      loginForm.setError("root", { message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const onRegister = async (data: RegisterData) => {
    setIsLoading(true);
    try {
      await register(data.name, data.email, data.password);
      toast({ title: t("auth.welcomeToast"), description: t("auth.accountReady") });
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t("auth.createAccountTab") + " failed. Please try again.";
      registerForm.setError("root", { message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const onForgot = async (data: ForgotData) => {
    setIsLoading(true);
    try {
      await apiPost("/api/auth/forgot-password", { email: data.email });
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle =
    mode === "forgot"
      ? (forgotSent ? t("auth.forgotSentTitle") : t("auth.forgotTitle"))
      : mode === "login"
      ? t("auth.welcomeBack")
      : t("auth.joinTitle");

  const headerSubtitle =
    mode === "forgot"
      ? (forgotSent ? "" : t("auth.forgotSubtitle"))
      : mode === "login"
      ? t("auth.loginSubtitle")
      : t("auth.registerSubtitle");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border border-[#DDD8D0] p-0 overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.10)]">

        {/* Header */}
        <div className="bg-gradient-to-br from-[#2D7A78]/10 to-[#2D7A78]/10 px-8 pt-8 pb-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <REKINDERLogo className="h-16 w-auto" />
          </div>
          <h2 className="font-display font-bold text-2xl text-[#1A3535]">{headerTitle}</h2>
          {headerSubtitle && (
            <p className="text-[#1A3535]/50 text-sm mt-1">{headerSubtitle}</p>
          )}
        </div>

        {/* Tab switcher — only for login / register */}
        {mode !== "forgot" && (
          <div className="flex border-b border-[#DDD8D0] bg-white">
            <button
              onClick={() => switchMode("login")}
              className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                mode === "login"
                  ? "text-[#2D7A78] border-b-2 border-[#2D7A78]"
                  : "text-muted-foreground hover:text-[#1A3535]/70"
              }`}
            >
              {t("auth.signInTab")}
            </button>
            <button
              onClick={() => switchMode("register")}
              className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                mode === "register"
                  ? "text-[#2D7A78] border-b-2 border-[#2D7A78]"
                  : "text-muted-foreground hover:text-[#1A3535]/70"
              }`}
            >
              {t("auth.createAccountTab")}
            </button>
          </div>
        )}

        {/* Sign In form */}
        {mode === "login" && (
          <form onSubmit={loginForm.handleSubmit(onLogin)} className="px-8 py-6 bg-white space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.emailLabel")}</label>
              <input
                {...loginForm.register("email")}
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                className={inputCls}
              />
              {loginForm.formState.errors.email && (
                <p className="text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-[#1A3535]">{t("auth.passwordLabel")}</label>
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-xs text-[#2D7A78] hover:underline font-medium"
                >
                  {t("auth.forgotPasswordLink")}
                </button>
              </div>
              <PasswordInput
                {...loginForm.register("password")}
                placeholder={t("auth.passwordPlaceholder")}
              />
              {loginForm.formState.errors.password && (
                <p className="text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            {loginForm.formState.errors.root && (
              <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                {loginForm.formState.errors.root.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#7EB6B6] hover:bg-[#6aa3a3] text-[#1A3535] font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : t("auth.signInButton")}
            </button>

            <p className="text-center text-xs text-[#1A3535]/40 pt-1">
              {t("auth.newHere")}{" "}
              <button type="button" onClick={() => switchMode("register")} className="text-[#2D7A78] font-semibold hover:underline">
                {t("auth.createAccount")}
              </button>
            </p>
          </form>
        )}

        {/* Create Account form */}
        {mode === "register" && (
          <form onSubmit={registerForm.handleSubmit(onRegister)} className="px-8 py-6 bg-white space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.fullNameLabel")}</label>
              <input
                {...registerForm.register("name")}
                placeholder={t("auth.fullNamePlaceholder")}
                className={inputCls}
              />
              {registerForm.formState.errors.name && (
                <p className="text-xs text-red-500">{registerForm.formState.errors.name.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.emailLabel")}</label>
              <input
                {...registerForm.register("email")}
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                className={inputCls}
              />
              {registerForm.formState.errors.email && (
                <p className="text-xs text-red-500">{registerForm.formState.errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.passwordLabel")}</label>
              <PasswordInput
                {...registerForm.register("password")}
                placeholder={t("auth.passwordHint")}
              />
              {registerForm.formState.errors.password && (
                <p className="text-xs text-red-500">{registerForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.confirmPasswordLabel")}</label>
              <PasswordInput
                {...registerForm.register("confirmPassword")}
                placeholder={t("auth.confirmPasswordPlaceholder")}
              />
              {registerForm.formState.errors.confirmPassword && (
                <p className="text-xs text-red-500">{registerForm.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            {registerForm.formState.errors.root && (
              <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                {registerForm.formState.errors.root.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#7EB6B6] hover:bg-[#6aa3a3] text-[#1A3535] font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : t("auth.createAccountButton")}
            </button>

            <p className="text-center text-xs text-[#1A3535]/40 pt-1">
              {t("auth.alreadyHaveAccount")}{" "}
              <button type="button" onClick={() => switchMode("login")} className="text-[#2D7A78] font-semibold hover:underline">
                {t("auth.signIn")}
              </button>
            </p>
          </form>
        )}

        {/* Forgot password form */}
        {mode === "forgot" && !forgotSent && (
          <form onSubmit={forgotForm.handleSubmit(onForgot)} className="px-8 py-6 bg-white space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-[#1A3535]">{t("auth.forgotEmailLabel")}</label>
              <input
                {...forgotForm.register("email")}
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                className={inputCls}
                autoFocus
              />
              {forgotForm.formState.errors.email && (
                <p className="text-xs text-red-500">{forgotForm.formState.errors.email.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#7EB6B6] hover:bg-[#6aa3a3] text-[#1A3535] font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : t("auth.forgotButton")}
            </button>

            <p className="text-center text-xs text-[#1A3535]/40 pt-1">
              <button type="button" onClick={() => switchMode("login")} className="text-[#2D7A78] font-semibold hover:underline">
                {t("auth.forgotBackToLogin")}
              </button>
            </p>
          </form>
        )}

        {/* Forgot password — email sent confirmation */}
        {mode === "forgot" && forgotSent && (
          <div className="px-8 py-8 bg-white text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 size={48} className="text-[#2D7A78]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#1A3535]/70 leading-relaxed">
              {t("auth.forgotSentMessage")}
            </p>
            <button
              type="button"
              onClick={() => switchMode("login")}
              className="w-full py-3.5 bg-[#7EB6B6] hover:bg-[#6aa3a3] text-[#1A3535] font-semibold rounded-xl transition-colors"
            >
              {t("auth.forgotBackToLogin")}
            </button>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
